import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "@/lib/utils"

// Simplified component that doesn't try to forward refs unnecessarily
const SheetNoOverlay = SheetPrimitive.Root

// Custom Portal that doesn't include the overlay
const SheetNoOverlayPortal = SheetPrimitive.Portal

// Add SheetTitle and SheetHeader for accessibility
const SheetNoOverlayTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
))
SheetNoOverlayTitle.displayName = SheetPrimitive.Title.displayName

const SheetNoOverlayHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
SheetNoOverlayHeader.displayName = "SheetNoOverlayHeader"

// Use completely clear styling with just a white content panel
const sheetVariants = cva(
  "fixed z-50 gap-4 p-0 transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
)

interface SheetNoOverlayContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
  VariantProps<typeof sheetVariants> { }

const SheetNoOverlayContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetNoOverlayContentProps
>(({ side = "right", className, children, ...props }, ref) => {
  // Use this to remove overlay after component mounts
  React.useEffect(() => {
    // Add a simple style tag to the document head
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      [data-state="open"][role="presentation"] {
        background-color: transparent !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(styleEl);
    
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);
  
  return (
    <SheetNoOverlayPortal>
      {/* No SheetOverlay component here */}
      <SheetPrimitive.Content
        ref={ref}
        className={cn(sheetVariants({ side }), className)}
        {...props}
      >
        <div className="bg-white/90 dark:bg-gray-900/90 h-full w-full rounded-l-lg border-l border-gray-200/50 dark:border-gray-800/50 shadow-xl p-6 pointer-events-auto backdrop-blur-sm">
          {children}
        </div>
      </SheetPrimitive.Content>
    </SheetNoOverlayPortal>
  );
})
SheetNoOverlayContent.displayName = SheetPrimitive.Content.displayName

export { SheetNoOverlay, SheetNoOverlayContent, SheetNoOverlayTitle, SheetNoOverlayHeader } 